import React, { useEffect, useMemo, useRef, useState } from 'react';
import clsx from 'clsx';
import { useInView } from 'react-intersection-observer';
import type { VirtuosoGridHandle } from 'react-virtuoso';
import { VirtuosoGrid } from 'react-virtuoso';
import { ReleaseInfoCard } from '@components/SongCards/ReleaseInfoCard';
import { UpcomingRecentEmptyState } from '@components/UpcomingRecentEmpty';
import { GridSkeleton } from '@components/basics/GridSkeleton';
import { LoadingGIF } from '@components/basics/LoadingGif';
import { useAuth } from '@context/auth/base';
import { MediaQuery, useMediaQuery } from '@context/mediaQuery';
import type { FragmentType } from '@graphql/reactQuery';
import { getFragment, ReleaseInfoCardFragmentDoc } from '@graphql/reactQuery';
import { updateRecentScrollIndex, useReleaseStatus } from '@hooks/ReleaseStatus/useReleaseStatus';
import { usePopState } from '@hooks/usePopState';
import { GRID_STYLE } from '@utils/constants';
import { getNextTrackIds } from '@utils/helpers';
import { useRecentDrops } from '../../hooks/useRecentDrops';
import { RecentDropsPageHeader } from './RecentHeader';

const LAPTOP_SCREEN_GRID_MAX = 8;
const TABLET_SCREEN_GRID_MAX = 6;
const MOBILE_SCREEN_GRID_MAX = 4;

const LAPTOP_SCREEN_ROW_MAX = 4;
const TABLET_SCREEN_ROW_MAX = 3;
const MOBILE_SCREEN_ROW_MAX = 2;

const ItemContent = ({
  index,
  release,
  getNextTrackIds,
}: {
  index: number;
  release: FragmentType<ReleaseInfoCardFragmentDoc>;
  getNextTrackIds: () => string[];
}) => {
  const { inView, ref } = useInView({ rootMargin: '25% 45%' });
  const { isPopStateEvent } = usePopState();
  const { tabletLandscape, tablet } = MediaQuery.useContainer();

  const rowMax = tabletLandscape
    ? LAPTOP_SCREEN_ROW_MAX
    : tablet
    ? TABLET_SCREEN_ROW_MAX
    : MOBILE_SCREEN_ROW_MAX;

  useEffect(() => {
    if (isPopStateEvent) {
      return;
    }

    if (inView) {
      //more accurate row when user goes back
      //we based the scroll index on the first item in the last row that was in view
      const firstIndexInRow = Math.floor(index / rowMax) * rowMax - 1;
      updateRecentScrollIndex(firstIndexInRow);
    }
  }, [inView, index, isPopStateEvent, rowMax]);

  return (
    <div
      ref={ref}
      // this is to enhance the user experience when clicking on a card and going back to make sure the card is where we scroll to
      onClick={() => {
        updateRecentScrollIndex(index);
      }}
    >
      <ReleaseInfoCard
        key={`recent-drops-card-${index}-${getFragment(ReleaseInfoCardFragmentDoc, release).id}`}
        data={release}
        hideSocialProof={!tablet}
        getNextTrackIds={getNextTrackIds}
      />
    </div>
  );
};

const renderItemContent = (
  index: number,
  item: FragmentType<ReleaseInfoCardFragmentDoc>,
  getNextTrackIds: () => string[],
) => <ItemContent index={index} release={item} getNextTrackIds={getNextTrackIds} />;

export const RecentDropsPage = () => {
  const { isPopStateEvent } = usePopState();
  const { tabletLandscape, tablet } = useMediaQuery();
  const { authUser } = useAuth();
  const authUserId = authUser?.id;

  const { recent, recentFilter } = useReleaseStatus();

  const {
    recentDrops,
    loadMore,
    isInitialLoading,
    isFetchingNextPage,
    hasNextPage = false,
  } = useRecentDrops({
    filter: recentFilter,
  });

  const loadingNext = isFetchingNextPage && hasNextPage;

  const memoizedFooter = useMemo(() => (loadingNext ? <LoadingGIF /> : null), [loadingNext]);

  const gridRef = useRef<VirtuosoGridHandle>(null);

  const [popStateOccurred, setPopStateOccured] = useState(false);
  const [hasScrolled, setHasScrolled] = useState(false);

  // we split into two useEffects to address a race condition
  // where the popStateEvent was true but is set to false before the gridRef was set
  useEffect(() => {
    if (isPopStateEvent) {
      setPopStateOccured(true);
    }
  }, [isPopStateEvent]);

  const cardMax = tabletLandscape
    ? LAPTOP_SCREEN_GRID_MAX
    : tablet
    ? TABLET_SCREEN_GRID_MAX
    : MOBILE_SCREEN_GRID_MAX;

  useEffect(() => {
    if (popStateOccurred && recent.scrollIndex) {
      //this check is to prevent weird jumps when user is on the top of the page
      if (recent.scrollIndex > cardMax) {
        gridRef.current?.scrollToIndex(recent.scrollIndex);
      }
      setHasScrolled(true);
    }

    // this check is to prevent infinite scrolling. Once we know for user that a popStateOccured and have already scrolled to the index, we can reset the popStateOccurred
    if (hasScrolled && popStateOccurred) {
      setPopStateOccured(false);
    }
  }, [cardMax, hasScrolled, popStateOccurred, recent.scrollIndex]);

  return (
    <div
      className={clsx(
        // set px values here to match the search bar padding - tailwind values were a couple pixels off
        'mx-auto my-0  flex min-h-screen flex-col gap-4 px-[16px] ',
        'md:gap-2 md:px-[24px] md:pb-4',
        'lg: max-w-7xl',
        loadingNext ? 'pb-28 md:pb-40' : 'pb-[8px] md:pb-4',
      )}
    >
      <RecentDropsPageHeader loading={isInitialLoading} />

      {/* Key is needed to avoid virtuoso not fetching new pages when user logs in or filters change
        This is Virtuoso Grid specific. Virtuoso List does not have this issue */}
      <div className="md:mt-4" key={`recent-drops-${authUserId}-${JSON.stringify(recentFilter)}`}>
        {isInitialLoading ? (
          <GridSkeleton />
        ) : recentDrops.length ? (
          <VirtuosoGrid
            ref={gridRef}
            context={{ loadMore }}
            endReached={loadMore}
            data={recentDrops}
            useWindowScroll
            itemContent={(index, item) => {
              return renderItemContent(
                index,
                item,
                getNextTrackIds({ trackId: item.track.id, releases: recentDrops }),
              );
            }}
            overscan={700}
            listClassName={GRID_STYLE}
            components={{ Footer: () => memoizedFooter }}
          />
        ) : (
          <UpcomingRecentEmptyState type="recent" />
        )}
      </div>
    </div>
  );
};
